/**
 * useDrillsConfig.js
 *
 * Hook for managing vessel-specific drill configuration settings.
 * WBC3 Edition: 5 settings for drill management
 *
 * Settings:
 * 1. Default Drill Duration - Default 30 mins, range 15-120
 * 2. Due Soon Warning Days - Default 7, range 1-30
 * 3. Company Required Drills - Select additional drills beyond WBC3 minimums
 * 4. Require Notes for Failed Drills - Default ON (WBC3 compliance)
 * 5. Enable Onboard Training Tab - Default OFF
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';

// RBAC helper (same pattern as useDefectsConfig)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * WBC3 MANDATORY DRILLS - These are ALWAYS shown regardless of settings
 * SI 1999/2722 Regulation 8(2) requires Fire and Abandon Ship monthly
 * WBC3 Section 14.11.2 requires MOB drills "regularly routinely"
 */
export const WBC3_MANDATORY_DRILLS = [
  { type: 'fire', name: 'Fire Drill', frequencyDays: 30, complianceLevel: 'Required', source: 'WBC3 / SI 1999/2722' },
  { type: 'abandon_ship', name: 'Abandon Ship', frequencyDays: 30, complianceLevel: 'Required', source: 'WBC3 / SI 1999/2722' },
  { type: 'mob', name: 'Man Overboard (MOB)', frequencyDays: 90, complianceLevel: 'Required', source: 'WBC3 Section 14.11.2' }
];

/**
 * Optional drills that companies can choose to make mandatory
 */
export const OPTIONAL_DRILLS = [
  { type: 'pollution_response', name: 'Pollution Response', recommended: true, defaultFrequency: 180 },
  { type: 'medical_emergency', name: 'Medical Emergency', recommended: true, defaultFrequency: 180 },
  { type: 'flooding', name: 'Flooding/Damage Control', recommended: true, defaultFrequency: 180 },
  { type: 'collision', name: 'Collision Response', recommended: false, defaultFrequency: 180 },
  { type: 'grounding', name: 'Grounding', recommended: false, defaultFrequency: 180 },
  { type: 'steering_nav_fail', name: 'Steering/Navigation Failure', recommended: false, defaultFrequency: 90 },
  { type: 'electrical_blackout', name: 'Electrical Blackout', recommended: false, defaultFrequency: 180 },
  { type: 'enclosed_space_entry', name: 'Enclosed Space Entry', recommended: false, defaultFrequency: 180 },
  { type: 'towing_emergency', name: 'Towing Emergency', recommended: false, defaultFrequency: 180 }
];

/**
 * Default drills configuration
 */
const getDefaultConfig = () => ({
  version: 1,
  revision: 1,

  // Setting 1: Default drill duration in minutes
  defaultDrillDuration: 30,

  // Setting 2: Days before due to show warning
  dueSoonWarningDays: 7,

  // Setting 3: Company required drills (additional to WBC3 mandatory)
  companyRequiredDrills: [],

  // Setting 4: Custom drill frequencies (days) - override defaults
  drillFrequencies: {},

  // Setting 5: Require notes when drill outcome is Failed (WBC3 compliance)
  requireNotesForFailedDrills: true,

  // Setting 6: Enable separate onboard training tab
  enableTrainingSection: false,

  // Metadata
  lastModified: new Date().toISOString(),
  modifiedBy: { name: 'System', userId: 'system' }
});

/**
 * Hook for managing drills configuration
 */
export const useDrillsConfig = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Vessel-scoped storage
  const storageKey = 'drills_config';
  const { value: rawConfig, setValue: setConfig } = useStorageState(
    storageKey,
    getDefaultConfig()
  );

  // Ensure config has all required fields (handle upgrades)
  const config = useMemo(() => {
    const defaults = getDefaultConfig();
    return {
      ...defaults,
      ...rawConfig
    };
  }, [rawConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get list of company-required drills with their details
   */
  const companyRequiredDrillsWithDetails = useMemo(() => {
    return OPTIONAL_DRILLS.filter(drill =>
      (config.companyRequiredDrills || []).includes(drill.type)
    ).map(drill => ({
      ...drill,
      frequency: config.drillFrequencies[drill.type] || drill.defaultFrequency
    }));
  }, [config.companyRequiredDrills, config.drillFrequencies]);

  /**
   * Get frequency for a specific drill type
   */
  const getDrillFrequency = useCallback((drillType) => {
    // Check custom frequency first
    if (config.drillFrequencies[drillType]) {
      return config.drillFrequencies[drillType];
    }
    // Check optional drill defaults
    const optionalDrill = OPTIONAL_DRILLS.find(d => d.type === drillType);
    if (optionalDrill) {
      return optionalDrill.defaultFrequency;
    }
    // Default fallback
    return 90;
  }, [config.drillFrequencies]);

  /**
   * FT-112 REWRITE: Combined drill list for display
   * Merges WBC3 mandatory drills + company required drills
   * This is the SINGLE SOURCE OF TRUTH for what drills appear in the list
   * React handles reactivity automatically via useStorageState
   */
  const drillList = useMemo(() => {
    // Start with WBC3 mandatory drills (always shown)
    const mandatory = WBC3_MANDATORY_DRILLS.map(drill => ({
      ...drill,
      drillType: drill.type,
      frequencyDays: config.drillFrequencies[drill.type] || drill.frequencyDays,
      isMandatory: true
    }));

    // Add company required drills (not already in mandatory list)
    const mandatoryTypes = new Set(WBC3_MANDATORY_DRILLS.map(d => d.type));
    const company = (config.companyRequiredDrills || [])
      .filter(type => !mandatoryTypes.has(type))
      .map(type => {
        const optionalDrill = OPTIONAL_DRILLS.find(d => d.type === type);
        return {
          type,
          drillType: type,
          name: optionalDrill?.name || type,
          frequencyDays: config.drillFrequencies[type] || optionalDrill?.defaultFrequency || 90,
          complianceLevel: 'Required',
          source: 'Company SMS Policy',
          isMandatory: false
        };
      });

    return [...mandatory, ...company];
  }, [config.companyRequiredDrills, config.drillFrequencies]);

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Update a single setting
   */
  const updateSetting = useCallback(async (settingKey, value) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useDrillsConfig] Permission denied');
      return false;
    }

    const oldValue = config[settingKey];

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      [settingKey]: value,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'drills_config',
    //     {
    //       vesselId: currentVessel?.id,
    //       setting: settingKey,
    //       newValue: value,
    //       oldValue
    //     },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useDrillsConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Update default drill duration
   */
  const setDefaultDrillDuration = useCallback(async (minutes) => {
    const clamped = Math.min(120, Math.max(15, minutes));
    return updateSetting('defaultDrillDuration', clamped);
  }, [updateSetting]);

  /**
   * Update due soon warning days
   */
  const setDueSoonWarningDays = useCallback(async (days) => {
    const clamped = Math.min(30, Math.max(1, days));
    return updateSetting('dueSoonWarningDays', clamped);
  }, [updateSetting]);

  /**
   * Toggle a company required drill
   */
  const toggleCompanyDrill = useCallback(async (drillType, enabled) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useDrillsConfig] Permission denied');
      return false;
    }

    const currentDrills = config.companyRequiredDrills || [];
    let updatedDrills;

    if (enabled && !currentDrills.includes(drillType)) {
      updatedDrills = [...currentDrills, drillType];
    } else if (!enabled && currentDrills.includes(drillType)) {
      updatedDrills = currentDrills.filter(d => d !== drillType);
    } else {
      return true; // No change needed
    }

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      companyRequiredDrills: updatedDrills,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);
    // FT-112 REWRITE: No event dispatch needed - useStorageState handles reactivity
    // Any component using useDrillsConfig will automatically re-render

    return true;
  }, [can, config, setConfig, currentUser]);

  /**
   * Set custom frequency for a drill type
   */
  const setDrillFrequency = useCallback(async (drillType, days) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useDrillsConfig] Permission denied');
      return false;
    }

    const clamped = Math.min(365, Math.max(1, days));
    const updatedFrequencies = {
      ...config.drillFrequencies,
      [drillType]: clamped
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      drillFrequencies: updatedFrequencies,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);
    // FT-112 REWRITE: No event dispatch needed - useStorageState handles reactivity

    return true;
  }, [can, config, setConfig, currentUser]);

  /**
   * Toggle require notes for failed drills
   */
  const setRequireNotesForFailedDrills = useCallback(async (require) => {
    return updateSetting('requireNotesForFailedDrills', !!require);
  }, [updateSetting]);

  /**
   * Toggle training section
   */
  const setEnableTrainingSection = useCallback(async (enable) => {
    return updateSetting('enableTrainingSection', !!enable);
  }, [updateSetting]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useDrillsConfig] Permission denied');
      return false;
    }

    const defaults = getDefaultConfig();
    defaults.revision = (config.revision || 0) + 1;
    defaults.lastModified = new Date().toISOString();
    defaults.modifiedBy = { name: currentUser?.name, userId: currentUser?.id };

    await setConfig(defaults);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'drills_config',
    //     { vesselId: currentVessel?.id, action: 'reset_to_defaults' },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useDrillsConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Raw config
    config,

    // FT-112 REWRITE: Drill list for display (mandatory + company drills)
    // This is the SINGLE SOURCE OF TRUTH - use this in SafetyDrillsTab
    drillList,

    // Computed values
    companyRequiredDrillsWithDetails,
    getDrillFrequency,

    // Direct settings access
    defaultDrillDuration: config.defaultDrillDuration,
    dueSoonWarningDays: config.dueSoonWarningDays,
    companyRequiredDrills: config.companyRequiredDrills,
    drillFrequencies: config.drillFrequencies,
    requireNotesForFailedDrills: config.requireNotesForFailedDrills,
    enableTrainingSection: config.enableTrainingSection,

    // Individual mutations
    setDefaultDrillDuration,
    setDueSoonWarningDays,
    toggleCompanyDrill,
    setDrillFrequency,
    setRequireNotesForFailedDrills,
    setEnableTrainingSection,
    resetToDefaults,

    // Generic update
    updateSetting,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision,

    // Constants for UI
    OPTIONAL_DRILLS,
    WBC3_MANDATORY_DRILLS
  };
};

export default useDrillsConfig;
